/* WeeklyReportCard — AI weekly health digest (iter420.4 engagement hook).
 *
 * Whoop-style weekly summary. On-demand generation чрез POST към
 * /weekly-report endpoint. Pro+ виждат пълния "pro_full" анализ;
 * free user-ите виждат tease + upgrade CTA.
 */
import React, { useState } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, Calendar, Trophy, Target, Loader2, ArrowUp, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

export default function WeeklyReportCard() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/health-intelligence/weekly-report`);
      setReport(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка" : "Failed"));
    } finally {
      setLoading(false);
    }
  };

  const isPro = report?.tier === "pro";

  return (
    <Card className={`border overflow-hidden ${
          isDark ? "border-violet-500/30 bg-gradient-to-br from-violet-900/20 to-fuchsia-900/20"
                 : "border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50"
        }`} data-testid="weekly-report-card">
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-lg">
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <h3 className={`text-base font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                {bg ? "Седмичен AI здравен отчет" : "Weekly AI Health Report"}
              </h3>
              <p className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                {bg ? "Какво се промени тази седмица + 1 препоръка за следващата"
                    : "What changed this week + 1 next-week recommendation"}
              </p>
            </div>
          </div>
          {!report && (
            <Button onClick={handleGenerate} disabled={loading}
                    className="bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white"
                    data-testid="weekly-report-generate">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {bg ? "Генерирай отчет" : "Generate report"}
            </Button>
          )}
        </div>

        {/* Report content */}
        {!report && !loading && (
          <p className={`text-[11.5px] leading-relaxed ${isDark ? "text-slate-300" : "text-slate-700"}`}>
            {bg
              ? "Натисни 'Генерирай отчет' за да получиш AI-анализ на твоята последна седмица: промени в DIL/NIS, тенденции в симптомите, и конкретна препоръка за следващата седмица."
              : "Click 'Generate report' to get an AI analysis of your last week: DIL/NIS changes, symptom trends, and a concrete recommendation for next week."}
          </p>
        )}

        {loading && (
          <div className="flex items-center gap-2 py-4">
            <Loader2 className={`w-4 h-4 animate-spin ${isDark ? "text-violet-300" : "text-violet-600"}`} />
            <span className={`text-xs ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              {bg ? "AI анализира твоята седмица..." : "AI analyzing your week..."}
            </span>
          </div>
        )}

        {report && (
          <div className="space-y-3" data-testid="weekly-report-content">
            {/* Week changes */}
            <div className={`p-3 rounded-xl ${isDark ? "bg-slate-900/40" : "bg-white/80"}`}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Sparkles className={`w-3.5 h-3.5 ${isDark ? "text-violet-300" : "text-violet-600"}`} />
                <h4 className={`text-xs uppercase tracking-wider font-bold ${isDark ? "text-violet-300" : "text-violet-700"}`}>
                  {bg ? "Какво се промени" : "What changed"}
                </h4>
              </div>
              <p className={`text-[12px] leading-relaxed ${isDark ? "text-slate-200" : "text-slate-800"}`}
                 data-testid="weekly-report-changes">
                {report.week_changes}
              </p>
            </div>

            {/* Wins */}
            {(report.week_wins || []).length > 0 && (
              <div className={`p-3 rounded-xl ${isDark ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-emerald-50/60 border border-emerald-200"}`}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Trophy className={`w-3.5 h-3.5 ${isDark ? "text-emerald-300" : "text-emerald-600"}`} />
                  <h4 className={`text-xs uppercase tracking-wider font-bold ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                    {bg ? "Победи тази седмица" : "Wins this week"}
                  </h4>
                </div>
                <ul className="space-y-1" data-testid="weekly-report-wins">
                  {report.week_wins.map((w, i) => (
                    <li key={i} className={`text-[12px] leading-relaxed ${isDark ? "text-emerald-100" : "text-emerald-900"}`}>
                      ✓ {w}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Next focus */}
            <div className={`p-3 rounded-xl ${isDark ? "bg-amber-500/10 border border-amber-500/30" : "bg-amber-50/60 border border-amber-200"}`}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Target className={`w-3.5 h-3.5 ${isDark ? "text-amber-300" : "text-amber-600"}`} />
                <h4 className={`text-xs uppercase tracking-wider font-bold ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                  {bg ? "Фокус за следващата седмица" : "Next week focus"}
                </h4>
              </div>
              <p className={`text-[12px] leading-relaxed ${isDark ? "text-amber-100" : "text-amber-900"}`}
                 data-testid="weekly-report-focus">
                {report.next_focus}
              </p>
            </div>

            {/* Pro full analysis */}
            {isPro && report.pro_full && (
              <div className={`p-3 rounded-xl ${isDark ? "bg-violet-500/10 border border-violet-500/30" : "bg-violet-50/60 border border-violet-200"}`}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Sparkles className={`w-3.5 h-3.5 ${isDark ? "text-violet-300" : "text-violet-600"}`} />
                  <h4 className={`text-xs uppercase tracking-wider font-bold ${isDark ? "text-violet-300" : "text-violet-700"}`}>
                    {bg ? "Pro: дълбочинен анализ" : "Pro: deep analysis"}
                  </h4>
                </div>
                <p className={`text-[12px] leading-relaxed ${isDark ? "text-violet-100" : "text-violet-900"}`}
                   data-testid="weekly-report-pro-full">
                  {report.pro_full}
                </p>
              </div>
            )}

            {/* Free tier: Pro tease */}
            {!isPro && (
              <div className={`p-3 rounded-xl border-dashed border-2 ${isDark ? "border-violet-500/40 bg-violet-500/5" : "border-violet-300 bg-violet-50/40"}`}
                   data-testid="weekly-report-pro-tease">
                <div className="flex items-start gap-2.5">
                  <Lock className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isDark ? "text-violet-300" : "text-violet-600"}`} />
                  <div className="flex-1">
                    <h4 className={`text-xs font-bold ${isDark ? "text-violet-200" : "text-violet-900"}`}>
                      {bg ? "Заключен: Дълбочинен Pro анализ" : "Locked: Pro deep analysis"}
                    </h4>
                    <p className={`text-[11px] mt-1 leading-relaxed ${isDark ? "text-violet-200/80" : "text-violet-900/80"}`}>
                      {bg
                        ? "Pro потребителите получават дълбочинен анализ свързващ хранене, симптоми и conditions с конкретни биомаркери от лабораториите."
                        : "Pro users get a deep analysis connecting food, symptoms and conditions with biomarkers from labs."}
                    </p>
                    <Link to="/pricing">
                      <Button size="sm" className="mt-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white"
                              data-testid="weekly-report-upgrade-cta">
                        <ArrowUp className="w-3.5 h-3.5 mr-1" />
                        {bg ? "Премини на Pro" : "Upgrade to Pro"}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Regenerate */}
            <button onClick={handleGenerate}
                    className={`text-[10.5px] underline ${isDark ? "text-slate-500 hover:text-slate-300" : "text-slate-500 hover:text-slate-700"}`}
                    data-testid="weekly-report-regenerate">
              {bg ? "Регенерирай" : "Regenerate"}
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
